import { useState } from "react";
import { BookOpen, Pencil, TrendingUp, Link2, Unlink, CalendarCheck } from "lucide-react";
import { useLifeState } from "@/context/LifeStateContext";
import { measuresByPillar, pillarById, scoreOf, pillarSummary, targetLabel, fmtValue, faithHabitValues } from "@/lib/pillars";
import { scoreTone, statusStyles } from "@/lib/central";
import { iso } from "@/lib/wellness";
import { PageHeader, SectionCard } from "@/components/shared";
import { FormModal, NumberField, SelectField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const FAITH = pillarById.faith;
const isDayCount = (m) => /^days\//.test(m.unit || "");

const Sparkline = ({ points, color }) => {
  const vals = (points || []).map((p) => Number(p.value)).filter((n) => Number.isFinite(n));
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const w = 64, h = 20, step = w / (vals.length - 1);
  const d = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ");
  return <svg width={w} height={h} aria-hidden="true"><path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const ScoreBadge = ({ score }) => {
  if (score == null) return <span className="inline-flex items-center rounded-full bg-[color:rgba(23,35,31,0.06)] px-2 py-0.5 text-[11px] text-[var(--text-faint)]">—</span>;
  const s = statusStyles[scoreTone(score)];
  return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums" style={{ color: s.color, backgroundColor: s.bg }}>{score}</span>;
};

export default function FaithTracker() {
  const { state, update, loading } = useLifeState();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weeklyDraft, setWeeklyDraft] = useState({});

  if (loading || !state) return <div className="space-y-4"><Skeleton className="h-32 rounded-[14px]" /><Skeleton className="h-96 rounded-[14px]" /></div>;

  const fm = state.faithMeasures || {};
  const links = state.faithHabitLinks || {};
  const habitVals = faithHabitValues(state);
  const habits = state.habits || [];
  const habitName = (id) => habits.find((h) => h.id === id)?.name || "habit";
  const list = measuresByPillar("faith");
  const groups = [...new Set(list.map((m) => m.group))];

  const currentOf = (m) => (links[m.id] != null ? habitVals[m.id] : fm[m.id]?.value);
  const currentMap = {};
  list.forEach((m) => { const v = currentOf(m); if (v != null && v !== "") currentMap[m.id] = v; });
  const summary = pillarSummary("faith", currentMap);
  const weeklyList = list.filter((m) => m.cadence === "weekly");

  const openLog = (m) => { setDraft({ id: m.id, name: m.name, unit: m.unit, target: targetLabel(m), value: fm[m.id]?.value ?? "" }); setOpen(true); };
  const saveLog = () => {
    const v = draft.value;
    if (v === "" || v == null || !Number.isFinite(Number(v))) return;
    update((prev) => {
      const prevRec = (prev.faithMeasures || {})[draft.id] || {};
      const history = [...(prevRec.history || []), { date: iso(), value: Number(v) }].slice(-30);
      return { ...prev, faithMeasures: { ...(prev.faithMeasures || {}), [draft.id]: { value: Number(v), updatedAt: new Date().toISOString(), history } } };
    }, "Logged faith measure");
    setOpen(false);
  };

  const openLink = (m) => { setLinkDraft({ id: m.id, name: m.name, habitId: links[m.id] || (habits[0]?.id || "") }); setLinkOpen(true); };
  const saveLink = () => {
    if (!linkDraft.habitId) return toast.error("Create a habit in Command first");
    update((prev) => ({ ...prev, faithHabitLinks: { ...(prev.faithHabitLinks || {}), [linkDraft.id]: linkDraft.habitId } }), "Linked habit");
    setLinkOpen(false); toast.success("Linked — this now counts from your habit tracker");
  };
  const unlink = (id) => {
    update((prev) => { const next = { ...(prev.faithHabitLinks || {}) }; delete next[id]; return { ...prev, faithHabitLinks: next }; }, "Unlinked habit");
    toast.success("Unlinked");
  };

  const openWeekly = () => {
    const seed = {};
    weeklyList.forEach((m) => { if (links[m.id] == null) seed[m.id] = fm[m.id]?.value ?? ""; });
    setWeeklyDraft(seed); setWeeklyOpen(true);
  };
  const saveWeekly = () => {
    update((prev) => {
      const next = { ...(prev.faithMeasures || {}) };
      Object.entries(weeklyDraft).forEach(([id, v]) => {
        if (v === "" || v == null || !Number.isFinite(Number(v))) return;
        const prevRec = next[id] || {};
        const history = [...(prevRec.history || []), { date: iso(), value: Number(v) }].slice(-30);
        next[id] = { value: Number(v), updatedAt: new Date().toISOString(), history };
      });
      return { ...prev, faithMeasures: next };
    }, "Weekly faith log");
    setWeeklyOpen(false); toast.success("This week's rhythms logged");
  };

  const habitOpts = habits.map((h) => ({ value: h.id, label: h.name }));

  return (
    <div>
      <PageHeader title="Faith & Character" subtitle="Track your spiritual rhythms and character — this flows into Command Central automatically." testid="faith-tracker-header"
        actions={<Button variant="outline" size="sm" onClick={openWeekly} data-testid="weekly-log-button" className="border-[color:var(--border-subtle)] hover:bg-[color:rgba(31,115,85,0.08)]"><CalendarCheck className="h-4 w-4 mr-1.5 text-[var(--green)]" />Log this week</Button>} />

      <SectionCard className="mb-4" testid="faith-summary-card">
        <div className="flex items-center gap-4">
          <span className="grid place-items-center h-14 w-14 rounded-[14px] text-white shrink-0" style={{ backgroundColor: FAITH.color }}><BookOpen className="h-6 w-6" /></span>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]">Pillar score</div>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-3xl tabular-nums text-[var(--text)]" data-testid="faith-pillar-score">{summary.score == null ? "—" : summary.score}</span>
              <span className="text-sm text-[var(--text-muted)]">{summary.loggedCount}/{summary.total} measures logged</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-[var(--gold)]"><TrendingUp className="h-3.5 w-3.5" />Feeds the Pillar Performance BI</div>
        </div>
      </SectionCard>

      <div className="space-y-4">
        {groups.map((g) => (
          <SectionCard key={g} title={g} testid={`faith-group-${g.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="space-y-2">
              {list.filter((m) => m.group === g).map((m) => {
                const linked = links[m.id] != null;
                const val = currentOf(m);
                const score = scoreOf(m, val);
                const rec = fm[m.id];
                return (
                  <div key={m.id} className="flex items-center gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`faith-row-${m.id}`}>
                    <div className="min-w-0 flex-[2]">
                      <div className="text-sm text-[var(--text)] flex items-center gap-2"><span className="truncate">{m.name}</span><span className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] shrink-0">{m.cadence}</span></div>
                      <div className="text-[12px] text-[var(--text-muted)] truncate">{m.desc}</div>
                    </div>
                    <div className="hidden sm:flex items-center justify-center flex-1">
                      {!linked && rec?.history && rec.history.length > 1 ? <Sparkline points={rec.history} color={FAITH.color} /> : <span className="text-[11px] text-[var(--text-faint)]">{linked ? "auto" : "no trend yet"}</span>}
                    </div>
                    <div className="w-28 text-right">
                      {val != null && val !== "" ? <div className="text-sm text-[var(--text)] tabular-nums" data-testid={`faith-value-${m.id}`}>{fmtValue(m, val)}</div> : <div className="text-[12px] text-[var(--text-faint)]" data-testid={`faith-value-${m.id}`}>Not yet logged</div>}
                      {linked && <div className="text-[10px] text-[var(--gold)] mt-0.5 truncate">from habit · {habitName(links[m.id])}</div>}
                    </div>
                    <div className="w-20 text-right text-[12px] text-[var(--text-muted)] tabular-nums hidden md:block">{targetLabel(m)}</div>
                    <div className="w-12 flex justify-center"><ScoreBadge score={score} /></div>
                    <div className="flex items-center shrink-0">
                      {isDayCount(m) && (linked
                        ? <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--gold)]" onClick={() => unlink(m.id)} data-testid={`faith-unlink-${m.id}`} aria-label={`Unlink ${m.name}`}><Unlink className="h-3.5 w-3.5" /></Button>
                        : <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--green)]" onClick={() => openLink(m)} data-testid={`faith-link-${m.id}`} aria-label={`Link a habit to ${m.name}`}><Link2 className="h-3.5 w-3.5" /></Button>)}
                      {!linked && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openLog(m)} data-testid={`faith-log-${m.id}`} aria-label={`Log ${m.name}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        ))}
      </div>

      {draft && (
        <FormModal open={open} onOpenChange={setOpen} title={`Log · ${draft.name}`} onSubmit={saveLog} submitLabel="Save value" testid="faith-log-modal">
          <NumberField label={`Value (${draft.unit || ""})`} value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} full testid="faith-log-input" autoFocus />
          <div className="sm:col-span-2 text-[12px] text-[var(--text-muted)]">Target: <span className="text-[var(--text)]">{draft.target}</span></div>
        </FormModal>
      )}

      {linkDraft && (
        <FormModal open={linkOpen} onOpenChange={setLinkOpen} title={`Link a habit · ${linkDraft.name}`}
          description="Pick a habit from your Command habit tracker. Completed days in the period will count automatically." onSubmit={saveLink} submitLabel="Link habit" testid="faith-link-modal">
          {habitOpts.length ? (
            <SelectField label="Habit" value={linkDraft.habitId} onChange={(v) => setLinkDraft({ ...linkDraft, habitId: v })} options={habitOpts} full testid="faith-link-select" />
          ) : (
            <div className="sm:col-span-2 text-sm text-[var(--text-muted)]">You have no habits yet. Add one in the Command page's Habit tracker, then link it here.</div>
          )}
        </FormModal>
      )}

      <FormModal open={weeklyOpen} onOpenChange={setWeeklyOpen} title="Log this week" description="Fill your weekly spiritual rhythms in one pass. Linked measures update from habits automatically." onSubmit={saveWeekly} submitLabel="Save week" testid="weekly-log-modal">
        {weeklyList.filter((m) => links[m.id] == null).map((m) => (
          <NumberField key={m.id} label={`${m.name} (${m.unit})`} value={weeklyDraft[m.id] ?? ""} onChange={(e) => setWeeklyDraft({ ...weeklyDraft, [m.id]: e.target.value })} testid={`weekly-input-${m.id}`} hint={`Target ${m.target}`} />
        ))}
        {weeklyList.filter((m) => links[m.id] == null).length === 0 && (
          <div className="sm:col-span-2 text-sm text-[var(--text-muted)]">All weekly rhythms are linked to habits — they log themselves.</div>
        )}
      </FormModal>
    </div>
  );
}
